import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import api from '../api';
import { getAccessToken, setAccessToken } from '../api';

const AuthContext = createContext(null);

const BACKEND_URL = (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Trusted-device keys (must match Login.js)
const DEVICE_TOKEN_KEY = 'matchitup_device_token';
const DEVICE_EMAIL_KEY = 'matchitup_device_email';

// How many times to retry a transient 5xx / network error before giving up
const MAX_FETCH_RETRIES = 3;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAccessToken());
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const fetchingRef = useRef(false);

  // ─── Trusted-device silent recovery ──────────────────────────────────────
  // Tries POST /api/auth/auto-login with the stored device token.
  // Returns true and updates session state if recovery succeeds; false otherwise.
  const _tryTrustedDeviceFallback = useCallback(async () => {
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    const deviceEmail = localStorage.getItem(DEVICE_EMAIL_KEY);
    if (!deviceToken || !deviceEmail) return false;

    try {
      const res = await axios.post(
        `${API}/auth/auto-login`,
        { email: deviceEmail, device_token: deviceToken },
        { withCredentials: true }
      );
      if (res.data?.token) {
        setAccessToken(res.data.token);
        setToken(res.data.token);
        if (res.data.refresh_token) setRefreshToken(res.data.refresh_token);
        if (res.data.user) setUser(res.data.user);
        // Rotate device token if server issued a new one
        if (res.data.device_token) {
          localStorage.setItem(DEVICE_TOKEN_KEY, res.data.device_token);
        }
        return true;
      }
    } catch (_) {
      // Device token expired or revoked — purge it so Login.js stops trying
      localStorage.removeItem(DEVICE_TOKEN_KEY);
      localStorage.removeItem(DEVICE_EMAIL_KEY);
    }
    return false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handle logout / token-refresh events from api.js interceptor ─────────
  useEffect(() => {
    const handleLogout = () => {
      // Before kicking the user to /login, silently attempt the trusted-device
      // fallback. This eliminates the "kick → rubberband" race condition.
      (async () => {
        const recovered = await _tryTrustedDeviceFallback();
        if (!recovered) {
          setAccessToken(null);
          localStorage.removeItem('refresh_token');
          setToken(null);
          setRefreshToken(null);
          setUser(null);
        }
      })();
    };

    const handleTokenRefreshed = (event) => {
      const { token: newToken, refreshToken: newRefreshToken } = event.detail;
      setAccessToken(newToken || null);
      localStorage.removeItem('refresh_token');
      setToken(newToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
    };
  }, [_tryTrustedDeviceFallback]);

  useEffect(() => {
    if (!initialLoadComplete && !fetchingRef.current) {
      fetchingRef.current = true;
      fetchProfile();
    } else if (token && !user && !fetchingRef.current) {
      fetchingRef.current = true;
      fetchProfile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, initialLoadComplete]);

  // ─── fetchProfile — resilient to 5xx / network errors ────────────────────
  const fetchProfile = async (retryCount = 0) => {
    const settle = () => {
      setLoading(false);
      setInitialLoadComplete(true);
      fetchingRef.current = false;
    };

    try {
      const response = await api.get(`${API}/profile`, { timeout: 10000 });
      if (!getAccessToken()) await refreshAccessToken();
      setUser(response.data);
      setToken(getAccessToken());
      settle();
    } catch (error) {
      const status = error.response?.status;
      const isTransient = !status || status >= 500; // network timeout or 5xx

      if (status === 401) {
        // Try trusted-device token before clearing the session
        const recovered = await _tryTrustedDeviceFallback();
        if (!recovered) {
          setAccessToken(null);
          setToken(null);
          setRefreshToken(null);
          setUser(null);
        }
        settle();
      } else if (isTransient && retryCount < MAX_FETCH_RETRIES) {
        // Transient error — exponential back-off; user stays on their current page
        const delay = Math.pow(2, retryCount) * 1000; // 1 s → 2 s → 4 s
        fetchingRef.current = false;
        await new Promise(r => setTimeout(r, delay));
        fetchingRef.current = true;
        await fetchProfile(retryCount + 1);
        // Recursive call owns settle(); return here to skip double-settle
      } else {
        // Retries exhausted or non-auth/non-transient error.
        // Settle without clearing user — a live session survives a brief outage.
        settle();
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = response.data;
    setAccessToken(newToken || null);
    localStorage.removeItem('refresh_token');
    if (newRefreshToken) setRefreshToken(newRefreshToken);
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    fetchProfile();
    return response.data;
  };

  const loginWithToken = (newToken, userData, newRefreshToken = null) => {
    setAccessToken(newToken || null);
    localStorage.removeItem('refresh_token');
    if (newRefreshToken) setRefreshToken(newRefreshToken);
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    fetchProfile();
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
    const { token: newToken, refresh_token: newRefreshToken, user: newUser } = response.data;
    setAccessToken(newToken || null);
    if (newRefreshToken) setRefreshToken(newRefreshToken);
    setToken(newToken);
    setUser(newUser);
    setLoading(false);
    return newUser;
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* best effort */ }
    setAccessToken(null);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    localStorage.removeItem(DEVICE_EMAIL_KEY);
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  const updateProfile = async (profileData) => {
    const response = await axios.put(`${API}/profile`, profileData, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });
    setUser(response.data);
    return response.data;
  };

  const refreshUser = async () => { await fetchProfile(); };

  // ─── Manual token refresh — also attempts device fallback on failure ───────
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      const { access_token, refresh_token: newRefreshToken } = response.data;
      setAccessToken(access_token || null);
      localStorage.removeItem('refresh_token');
      setToken(access_token);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      return true;
    } catch (_) {
      // Attempt silent device-token recovery before clearing the session
      const recovered = await _tryTrustedDeviceFallback();
      if (recovered) return true;
      setAccessToken(null);
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      return false;
    }
  }, [_tryTrustedDeviceFallback]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      refreshToken,
      login,
      loginWithToken,
      register,
      logout,
      updateProfile,
      refreshUser,
      refreshAccessToken,
      loading,
      initialLoadComplete
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
